import type { ComputedUnifiedBalances } from './computeUnifiedBalances';

import { sumBalanceUsd } from '../../utils/sumBalances';

export type UsdBalanceTotals =
  | ComputedUsdBalanceTotals
  | PlaceholderUsdBalanceTotals;

export type ComputedUsdBalanceTotals = {
  totalUsdTokenBalance: number;
  totalUsdPositionBalance: number;
  totalNftExcludingPatronsUsdBalance: number;
  /** Total USD value specifically for patron NFTs */
  totalPatronNftUsdBalance: number;
  /** Total USD balance of tokens, positions, and NFTs */
  totalUsdBalance: number;
  totalUsdBalanceOfficialOnly: number;
  totalTokenAndPositionUsdBalance24hChange: number;
  totalTokenAndPositionUsdBalance24hPercentageChange: number;
  /** Total 24h change in USD for all assets (tokens, positions, NFTs) */
  totalPortfolioUsd24hChange: number;
  totalPortfolioUsd24hPercentageChange: number;
  isLoading: false;
  isStale: boolean;
};

export type PlaceholderUsdBalanceTotals = {
  totalUsdTokenBalance: undefined;
  totalUsdPositionBalance: undefined;
  totalNftExcludingPatronsUsdBalance: undefined;
  totalPatronNftUsdBalance: undefined;
  totalUsdBalance: undefined;
  totalUsdBalanceOfficialOnly: undefined;
  totalTokenAndPositionUsdBalance24hChange: undefined;
  totalTokenAndPositionUsdBalance24hPercentageChange: undefined;
  totalPortfolioUsd24hChange: undefined;
  totalPortfolioUsd24hPercentageChange: undefined;
  isLoading: true;
  isStale: undefined;
};

/**
 * Computes USD balance totals from unified balances.
 *
 * @param unifiedBalances - Computed unified balances
 * @param totalPerpsPnl24h - Total perps 24h PnL data, used to calculate portfolio USD value
 * @param includeLockedPatronNfts - Whether to include locked patron NFTs in totals
 * @returns Cached USD balance totals (without perps 24h data)
 */
export function computeUsdTotals(
  unifiedBalances: ComputedUnifiedBalances,
  totalPerpsPnl24h: number,
  includeLockedPatronNfts: boolean
): ComputedUsdBalanceTotals {
  const { balances, patronNfts, withdrawablePatronNfts } = unifiedBalances;

  const officialOnlyBalances = balances.filter((b) => b.assetId !== undefined);

  const { totalUsd: totalUsdBalanceOfficialOnly } =
    sumBalanceUsd(officialOnlyBalances);

  // Calculate totals (only for balances with rates)
  const tokenBalances = balances.filter((b) => b.type === 'token');
  const positionBalances = balances.filter((b) => b.type === 'position');
  // Separate patron NFTs from other NFTs
  const allNftBalances = balances.filter((b) => b.type === 'nft');
  const nftBalancesExcludingPatrons = allNftBalances.filter(
    (b) => b.assetId !== 'patron_nft'
  );

  // Calculate total USD value for tokens
  const {
    totalUsd: totalUsdTokenBalance,
    total24hChange: totalTokenUsdBalance24hChange,
  } = sumBalanceUsd(tokenBalances);

  // Calculate total USD value for NFTs (excluding patron NFTs which are handled separately)
  const {
    totalUsd: totalNftExcludingPatronsUsdBalance,
    total24hChange: totalNftExcludingPatronsUsdBalance24hChange,
  } = sumBalanceUsd(nftBalancesExcludingPatrons);

  // Calculate patron NFT value separately, filtering based on preferences
  const patronNftData = includeLockedPatronNfts
    ? patronNfts
    : withdrawablePatronNfts;

  const totalPatronNftUsdBalance = patronNftData?.balanceUsd ?? 0;
  const totalPatronNftUsdBalance24hChange =
    patronNftData?.balance24hChangeUsd ?? 0;

  // Calculate total USD value for positions (only for balances with rates)
  const {
    totalUsd: totalUsdPositionBalance,
    total24hChange: totalUsdPositionBalance24hChange,
  } = sumBalanceUsd(positionBalances);

  // Calculate total USD value including NFTs (now includes filtered patron NFTs)
  const totalUsdBalanceIncludingNfts =
    (totalUsdTokenBalance ?? 0) +
    (totalNftExcludingPatronsUsdBalance ?? 0) +
    (totalPatronNftUsdBalance ?? 0) +
    (totalUsdPositionBalance ?? 0);

  const totalTokenAndPositionUsdBalance24hPercentageChange =
    totalUsdTokenBalance + totalUsdPositionBalance === 0
      ? 0
      : (100 *
          (totalTokenUsdBalance24hChange + totalUsdPositionBalance24hChange)) /
        (totalUsdTokenBalance + totalUsdPositionBalance);

  const totalPortfolioUsd24hChange =
    totalTokenUsdBalance24hChange +
    totalUsdPositionBalance24hChange +
    totalNftExcludingPatronsUsdBalance24hChange +
    totalPatronNftUsdBalance24hChange +
    totalPerpsPnl24h;

  const totalPortfolioUsd24hPercentageChange =
    totalUsdBalanceIncludingNfts === 0
      ? 0
      : (100 * totalPortfolioUsd24hChange) / totalUsdBalanceIncludingNfts;

  return {
    totalUsdTokenBalance,
    totalUsdPositionBalance,
    totalNftExcludingPatronsUsdBalance,
    totalPatronNftUsdBalance,
    totalUsdBalance: totalUsdBalanceIncludingNfts,
    totalUsdBalanceOfficialOnly,
    totalTokenAndPositionUsdBalance24hChange:
      totalTokenUsdBalance24hChange + totalUsdPositionBalance24hChange,
    totalTokenAndPositionUsdBalance24hPercentageChange:
      totalTokenAndPositionUsdBalance24hPercentageChange,
    totalPortfolioUsd24hChange,
    totalPortfolioUsd24hPercentageChange,
    isLoading: false,
    isStale: unifiedBalances.isStale,
  };
}
